import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { theme } from '../theme';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../hooks/useResponsive';
import { useNotification } from './Notification';
import { AppHeader } from './AppHeader';
import { QuickRateSheet } from './QuickRateSheet';
import { MatchEffortSection } from './match/MatchEffortSection';
import { getMatchResultDisplay } from '../types';

interface MatchPlayer {
  id: string;
  name: string;
  userId?: string;
  isSubstitute?: boolean;
  user?: { avatar?: string; nickname?: string; name?: string };
}

interface MatchData {
  id: string;
  location: string;
  team1Name: string;
  team2Name: string;
  team1Goals: number | null;
  team2Goals: number | null;
  scoreMode?: string | null;
  team1Color?: string;
  team2Color?: string;
  status: string;
  players: MatchPlayer[];
}

interface ReviewSummary {
  reviewedPlayerId: string;
  positiveTags: string[];
  negativeTags: string[];
}

interface UserMatchReviewsResponse {
  matchId: string;
  userId: string;
  reviewedPlayers: Record<
    string,
    {
      hasReviewed: boolean;
      reviewId: string;
      createdAt: string;
      positiveTags: string[];
      negativeTags: string[];
      customPositiveTags: string[];
      customNegativeTags: string[];
    }
  >;
}

interface AvailableTags {
  predefined: { positive: string[]; negative: string[] };
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const RatePostMatchPage: React.FC = () => {
  const { id: matchId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { showNotification } = useNotification();

  const [match, setMatch] = useState<MatchData | null>(null);
  const [myReviews, setMyReviews] = useState<Map<string, ReviewSummary>>(new Map());
  const [tags, setTags] = useState<AvailableTags>({ predefined: { positive: [], negative: [] } });
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState<MatchPlayer | null>(null);

  const fetchAll = useCallback(async () => {
    if (!matchId || !user?.id) return;
    setLoading(true);
    try {
      const [matchRes, reviewsRes, tagsRes] = await Promise.all([
        fetch(getApiUrl(`/api/matches/${matchId}`), { headers: authHeaders() }),
        fetch(getApiUrl(`/api/reviews/user/${user.id}/match/${matchId}`), {
          headers: authHeaders(),
        }),
        fetch(getApiUrl('/api/reviews/tags'), { headers: authHeaders() }),
      ]);

      if (matchRes.ok) {
        const matchData: MatchData = await matchRes.json();
        setMatch(matchData);
      }

      if (reviewsRes.ok) {
        const reviewsData: UserMatchReviewsResponse = await reviewsRes.json();
        const map = new Map<string, ReviewSummary>();
        for (const [playerId, data] of Object.entries(reviewsData.reviewedPlayers || {})) {
          map.set(playerId, {
            reviewedPlayerId: playerId,
            positiveTags: data.positiveTags || [],
            negativeTags: data.negativeTags || [],
          });
        }
        setMyReviews(map);
      }

      if (tagsRes.ok) {
        const tagsData: AvailableTags = await tagsRes.json();
        setTags(tagsData);
      }
    } catch {
      showNotification('No se pudo cargar el partido', 'error');
    } finally {
      setLoading(false);
    }
  }, [matchId, user?.id, showNotification]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const otherPlayers = useMemo(() => {
    if (!match) return [];
    return match.players.filter(
      (p) => !p.isSubstitute && p.userId && p.userId !== user?.id
    );
  }, [match, user?.id]);

  const ratedCount = useMemo(
    () => otherPlayers.filter((p) => myReviews.has(p.id)).length,
    [otherPlayers, myReviews]
  );
  const totalCount = otherPlayers.length;
  const allRated = ratedCount === totalCount && totalCount > 0;

  const handleSaveReview = async (selected: { positive: string[]; negative: string[] }) => {
    if (!selectedPlayer || !matchId) return;
    try {
      const res = await fetch(getApiUrl('/api/reviews'), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          matchId,
          reviewedPlayerId: selectedPlayer.id,
          comment: '',
          positiveTags: selected.positive,
          negativeTags: selected.negative,
          customPositiveTags: [],
          customNegativeTags: [],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || 'No se pudo guardar', 'error');
        return;
      }
      setMyReviews((curr) => {
        const next = new Map(curr);
        next.set(selectedPlayer.id, {
          reviewedPlayerId: selectedPlayer.id,
          positiveTags: selected.positive,
          negativeTags: selected.negative,
        });
        return next;
      });
      showNotification('Listo', 'success');
    } catch {
      showNotification('Error de conexión', 'error');
    }
  };

  const handleDone = () => navigate(`/match/${matchId}`);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: theme.colors.background }}>
        <AppHeader title="Calificá el partido" showBackButton />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '80px 16px',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid transparent',
              borderTop: `3px solid ${theme.colors.primary}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
        </div>
        <style>{`@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }`}</style>
      </div>
    );
  }

  if (!match) {
    return (
      <div style={{ minHeight: '100vh', background: theme.colors.background }}>
        <AppHeader title="Calificá el partido" showBackButton />
        <div
          style={{
            textAlign: 'center',
            padding: '80px 20px',
            color: theme.colors.textSecondary,
          }}
        >
          Partido no encontrado
        </div>
      </div>
    );
  }

  // "3 - 1" si se cargó el marcador; "Ganó X por 2 goles" si se cargó por diferencia.
  const scoreText =
    match.team1Goals != null && match.team2Goals != null
      ? getMatchResultDisplay(match).label
      : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.colors.background,
        padding: isMobile ? '70px 12px 100px' : '20px 24px 100px',
      }}
    >
      <AppHeader title="Calificá el partido" showBackButton />

      {match.location && (
        <div
          style={{
            fontSize: '13px',
            color: theme.colors.textSecondary,
            margin: '-4px 4px 12px',
          }}
        >
          📍 {match.location}
        </div>
      )}

      {scoreText && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '14px 20px',
            background: theme.colors.surface,
            borderRadius: '14px',
            border: `1px solid ${theme.colors.border}`,
            marginBottom: '14px',
          }}
        >
          <span
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 600,
              color: match.team1Color || theme.colors.primary,
            }}
          >
            {match.team1Name}
          </span>
          <span style={{ fontSize: '22px', fontWeight: 700, color: theme.colors.text }}>
            {scoreText}
          </span>
          <span
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 600,
              color: match.team2Color || '#2196F3',
            }}
          >
            {match.team2Name}
          </span>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 4px 12px',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <span style={{ fontWeight: 600, color: theme.colors.text, fontSize: '15px' }}>
          🔥 {ratedCount}/{totalCount} calificados
        </span>
        {allRated && (
          <span style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.primary }}>
            Todo listo, podés salir
          </span>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile
            ? 'repeat(3, 1fr)'
            : 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '10px',
        }}
      >
        {otherPlayers.map((p) => {
          const review = myReviews.get(p.id);
          const tagsCount = review ? review.positiveTags.length + review.negativeTags.length : 0;
          const isRated = !!review;
          const display = p.user?.nickname || p.name.split(' ')[0];
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedPlayer(p)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 8px',
                gap: '6px',
                aspectRatio: '0.9',
                borderRadius: '14px',
                background: theme.colors.surface,
                border: `${isRated ? 2 : 1}px solid ${isRated ? theme.colors.primary : theme.colors.border}`,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (!isRated) {
                  e.currentTarget.style.borderColor = theme.colors.primary;
                }
              }}
              onMouseLeave={(e) => {
                if (!isRated) {
                  e.currentTarget.style.borderColor = theme.colors.border;
                }
              }}
            >
              {p.user?.avatar ? (
                <img
                  src={p.user.avatar}
                  alt={display}
                  style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    backgroundColor: theme.colors.primary,
                    color: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                    fontWeight: 700,
                  }}
                >
                  {p.name[0]?.toUpperCase() || '?'}
                </div>
              )}
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: theme.colors.text,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {display}
              </span>
              {isRated ? (
                <div style={{ display: 'flex', gap: '3px' }}>
                  {Array.from({ length: tagsCount }).map((_, i) => (
                    <span
                      key={i}
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: theme.colors.primary,
                        display: 'inline-block',
                      }}
                    />
                  ))}
                </div>
              ) : (
                <span style={{ fontSize: '11px', color: theme.colors.textSecondary, opacity: 0.7 }}>
                  Tap
                </span>
              )}
            </button>
          );
        })}
      </div>

      {matchId && <MatchEffortSection matchId={matchId} />}

      {/* Footer CTA */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '12px 16px',
          borderTop: `1px solid ${theme.colors.border}`,
          background: theme.colors.background,
          zIndex: 10,
        }}
      >
        <button
          type="button"
          onClick={handleDone}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '12px',
            border: `1px solid ${allRated ? theme.colors.primary : theme.colors.border}`,
            background: allRated ? theme.colors.primary : theme.colors.surface,
            color: allRated ? '#1a1a1a' : theme.colors.text,
            fontSize: '15px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {allRated ? '¡Listo, ver partido!' : `Salir (${ratedCount}/${totalCount})`}
        </button>
      </div>

      <QuickRateSheet
        isOpen={!!selectedPlayer}
        player={
          selectedPlayer
            ? {
                id: selectedPlayer.id,
                name: selectedPlayer.user?.nickname || selectedPlayer.name,
                avatar: selectedPlayer.user?.avatar,
              }
            : null
        }
        positiveTags={tags.predefined.positive}
        negativeTags={tags.predefined.negative}
        existingReview={selectedPlayer ? myReviews.get(selectedPlayer.id) || null : null}
        onClose={() => setSelectedPlayer(null)}
        onSave={handleSaveReview}
      />
    </div>
  );
};

export default RatePostMatchPage;
